import React, { useMemo, useState } from 'react';
import { Alert, Card, Col, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import ServerError from '../../../../components/ServerError';
import { SkeletonTable } from '../../components/Skeleton/Table';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { useFlairBreadcrumbHook } from '../../../../hooks/useFlairBreadcrumbHook';
import { ApplyMeritControl } from './components/ApplyMeritControl';
import { CompensationReviewActions } from './components/CompensationReviewActions';
import { CompensationReviewInformation } from './components/CompensationReviewInformation';
import { CompensationReviewItemsTable } from './components/CompensationReviewItemsTable';
import { CompensationReviewResults } from './components/CompensationReviewResults';
import { CompensationReviewToolbar } from './components/CompensationReviewToolbar';
import { canEdit, isManagedInSalesforceOnly } from './logic';
import { useCompensationReviewDetail } from './useCompensationReviews';
import './CompensationReviews.css';

type Props = {
  id: string;
  // URL of the list this detail belongs to (People subtab vs standalone).
  listUrl: string;
};

export const CompensationReviewDetail: React.FC<Props> = ({ id, listUrl }) => {
  const history = useHistory();
  const t = useNamespacedTranslation('compensationReviews');
  const { review, loading, error, actions } = useCompensationReviewDetail(id);
  const [filter, setFilter] = useState('');

  useFlairBreadcrumbHook([
    { label: t('title'), url: listUrl },
    { label: review?.name ?? '' },
  ]);

  const goToList = () => history.push(listUrl);

  const filteredItems = useMemo(() => {
    if (!review) {
      return [];
    }
    const search = filter.trim().toLowerCase();
    return search
      ? review.items.filter((item) =>
          item.employee.name.toLowerCase().includes(search),
        )
      : review.items;
  }, [review, filter]);

  if (error) {
    return <ServerError />;
  }
  if (loading && !review) {
    return (
      <>
        <PageHeader
          title={t('title')}
          goBackButtonAction={goToList}
          alignBackButton
        />
        <SkeletonTable columns={6} rows={5} />
      </>
    );
  }
  if (!review) {
    return (
      <>
        <PageHeader
          title={t('title')}
          goBackButtonAction={goToList}
          alignBackButton
        />
        <Alert variant="warning">{t('detail.notFound')}</Alert>
      </>
    );
  }

  const editable = canEdit(review);
  const showIncludeColumn = review.status === 'PREPARING';

  return (
    <>
      <PageHeader
        title={review.name}
        subtitle={t('title')}
        goBackButtonAction={goToList}
        alignBackButton
        actions={
          <CompensationReviewActions
            review={review}
            isProcessing={actions.isProcessing}
            onSubmit={() => actions.submitForApproval()}
            onApprove={() => actions.approve()}
            onReject={() => actions.reject()}
          />
        }
      />

      {isManagedInSalesforceOnly(review) && (
        <Alert variant="info">{t('detail.salesforceOnlyNotice')}</Alert>
      )}

      <Row className="mb-4">
        <Col lg={8} className="d-flex flex-column">
          <CompensationReviewInformation review={review} />
          {editable && (
            <ApplyMeritControl
              onApplyMeritToAll={(meritPercent) =>
                actions.applyMeritToAll(meritPercent)
              }
            />
          )}
        </Col>
        <Col lg={4}>
          <CompensationReviewResults
            items={review.items}
            currency={review.currency}
          />
        </Col>
      </Row>

      <Card>
        <Card.Body className="pb-0">
          <CompensationReviewToolbar
            filter={filter}
            onFilterChange={setFilter}
          />
        </Card.Body>
        <CompensationReviewItemsTable
          // Key by review id so the grid (and its local sort state) resets to
          // the default when navigating between reviews — the detail component
          // is reused across :id changes rather than remounted.
          key={review.id}
          items={filteredItems}
          currency={review.currency}
          editable={editable}
          showIncludeColumn={showIncludeColumn}
          onNewAmountChange={(itemId, newAmount) =>
            actions.setNewAmount(itemId, newAmount)
          }
          onIncludedChange={(itemId, included) =>
            actions.setIncluded(itemId, included)
          }
        />
      </Card>
    </>
  );
};
